<template>
  <div class="statistics-header">
    <div class="statistics-header__range">
      <slot name="prepend" />
      <DatePickerField :defaultDates="defaultDates" label="Zeitraum" range @change="(date) => handleDateChange(date)"/>
      <div v-if="!hideComparison" class="statistics-header__comparison">
        <div class="statistics-header__comparison-label">Vergleiche mit</div>
        <div class="d-flex align-center">
          <v-btn-toggle v-model="comparisonMode" mandatory dense color="primary">
            <v-btn small value="period">{{ previousPeriodLabel }}</v-btn>
            <v-btn small value="date">Vorjahr</v-btn>
            <v-btn small value="weekday">Vorjahr (WT)</v-btn>
            <v-btn v-if="comparisonMode === 'custom'" small value="custom" @click="openCustomMenu">
              <v-icon x-small class="mr-1">mdi-calendar-range</v-icon>
              Eigener
            </v-btn>
          </v-btn-toggle>
          <!-- Eigener Vergleichszeitraum (Kalender) -->
          <v-menu
            v-model="customMenu"
            :close-on-content-click="false"
            offset-y
            left
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="ml-1"
                :color="comparisonMode === 'custom' ? 'primary' : ''"
                title="Eigenen Vergleichszeitraum wählen"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon small>mdi-calendar-range</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-date-picker
                v-model="customDates"
                range
                no-title
                color="primary"
                :first-day-of-week="1"
                locale="de-DE"
              />
              <v-card-actions class="pt-0">
                <v-spacer />
                <v-btn text small @click="customMenu = false">Abbrechen</v-btn>
                <v-btn
                  color="primary"
                  small
                  depressed
                  :disabled="!customDates.length"
                  @click="applyCustomComparison"
                >
                  Übernehmen
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-menu>
        </div>
        <div class="statistics-header__comparison-range">
          vs. {{ comparisonRangeText }}
          <v-progress-circular v-if="statisticComparisonLoading" class="ml-1" indeterminate size="10" width="2" />
        </div>
      </div>
    </div>
    <div class="statistics-header__right">
      <div class="statistics-header__presets d-none d-lg-flex d-xl-flex">
        <v-btn small v-for="item in presets" :key="item.value" @click="handlePresetClick(item.value)" :color="isSelectedPreset(item.value) ? 'primary' : ''" :text="!isSelectedPreset(item.value)">{{item.title}}</v-btn>
      </div>
      <slot name="append" />
    </div>
    <div class="d-lg-none d-xl-none ml-3">
      <v-menu v-model="openPresets">
        <template v-slot:activator="{ props }">
          <v-btn
            outlined
            color="primary"
            v-bind="props"
            @click="openPresets = !openPresets"
          >
            {{ selectedPresetName }}
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </template>

        <v-list>
          <v-list-item
            v-for="item in presets"
            :key="item.value"
            @click="handlePresetClick(item.value)"
          >
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </div>
  </div>
</template>

<script>
import DatePickerField from "@/components/DatePickerField";
import {mapActions, mapGetters, mapMutations} from "vuex";
import dayjs from "dayjs";

const utc = require('dayjs/plugin/utc'); // Import UTC plugin
const timezone = require('dayjs/plugin/timezone'); // Import timezone plugin
const weekday = require('dayjs/plugin/weekday'); // Import timezone plugin

dayjs.extend(utc); // Enable UTC plugin
dayjs.extend(timezone); // Enable timezone plugin
dayjs.extend(weekday); // Enable timezone plugin

export default {
  name: "StatisticsHeader",
  props: {
    // Views without comparison badges (e.g. Ad Insights) drop the toggle.
    hideComparison: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      preset: 'today',
      presets: [
        {
          title: "Today",
          value: "today"
        },
        {
          title: "Yesterday",
          value: "yesterday"
        },{
          title: "This Week",
          value: "thisweek"
        },{
          title: "This Month",
          value: "thismonth"
        },{
          title: "Last 7 Days",
          value: "last7days"
        },{
          title: "Last 30 Days",
          value: "last30days"
        },
      ],
      openPresets: false,
      customMenu: false,
      customDates: [],
    }
  },
  components: {DatePickerField},
  computed: {
    ...mapGetters([
      "statisticRange",
      "statisticsDayRange",
      "statisticComparisonMode",
      "statisticComparisonRange",
      "statisticComparisonLoading",
    ]),
    comparisonMode: {
      get() {
        return this.statisticComparisonMode
      },
      set(mode) {
        if (mode && mode !== this.statisticComparisonMode) {
          this.updateComparisonMode(mode)
        }
      }
    },
    previousPeriodLabel() {
      const days = this.statisticsDayRange + 1 // inclusive day count
      if (days === 1) return 'Vortag'
      if (days === 7) return 'Vorwoche'
      return 'Vorperiode'
    },
    defaultDates() {
      return [
        this.statisticRange.min.split("T")[0],
        this.statisticRange.max.split("T")[0]
      ]
    },
    selectedPresetName() {
      return this.presets.find(pre => pre.value === this.preset)?.title || 'Presets'
    },
    comparisonRangeText() {
      const format = (dateStr) => dayjs(dateStr).format('DD.MM.YYYY')
      const { min, max } = this.statisticComparisonRange
      return min.split('T')[0] === max.split('T')[0]
        ? format(min)
        : `${format(min)} – ${format(max)}`
    }
  },
  watch: {
    customMenu(open) {
      if (!open) return
      // Picker zeigt beim Oeffnen den aktuell aktiven Vergleichszeitraum
      const { min, max } = this.statisticComparisonRange
      const start = min.split("T")[0]
      const end = max.split("T")[0]
      this.customDates = start === end ? [start] : [start, end]
    },
  },
  methods: {
    ...mapActions(["loadStatistic", "updateComparisonMode", "updateCustomComparisonRange"]),
    openCustomMenu() {
      this.customMenu = true
    },
    applyCustomComparison() {
      if (!this.customDates.length) return
      // v-date-picker range: 1 Datum = einzelner Tag, 2 Daten ggf. unsortiert
      const sorted = [...this.customDates].sort()
      const start = sorted[0]
      const end = sorted[sorted.length - 1]
      const parse = (dateStr) => dayjs(dateStr).tz("Europe/Berlin", true)
      this.updateCustomComparisonRange({
        min: parse(start).startOf("day").format(),
        max: parse(end).endOf("day").format(),
      })
      this.customMenu = false
    },
    ...mapMutations(["setStatisticRange"]),
    handleDateChange(date) {
      this.preset = null
      const parseDate = (dateStr) =>
        dayjs(dateStr).tz("Europe/Berlin", true);

      const startDate = parseDate(date[0]);
      const endDate = parseDate(date[1]);

      const startOfDay = startDate.startOf("day").format();
      const endOfDay = endDate.endOf("day").format();

      this.setStatisticRange({
        min: startOfDay,
        max: endOfDay,
      });

      this.loadStatistic()
    },
    handlePresetClick(preset) {
      const localizedDayJs = dayjs().tz("Europe/Berlin", true)

      let startDate = localizedDayJs;
      let endDate = localizedDayJs;

      switch (preset) {
        case 'today':
          startDate = localizedDayJs;
          endDate = localizedDayJs;
          break;
        case 'yesterday':
          startDate = localizedDayJs.subtract(1, 'day');
          endDate = localizedDayJs.subtract(1, 'day');
          break;
        case 'last7days':
          startDate = localizedDayJs.subtract(7, 'day');
          endDate = localizedDayJs;
          break;
        case 'last30days':
          startDate = localizedDayJs.subtract(30, 'day');
          endDate = localizedDayJs;
          break;
        case 'thisweek':
          startDate = localizedDayJs.weekday(1);
          endDate = localizedDayJs;
          break;
        case 'thismonth':
          startDate = localizedDayJs.startOf('month');
          endDate = localizedDayJs;
          break;
        default:
          break;
      }

      if (startDate && endDate) {
        const startOfDay = startDate.startOf("day").format();
        const endOfDay = endDate.endOf("day").format();


        this.setStatisticRange({
          min: startOfDay,
          max: endOfDay,
        });

        this.loadStatistic();
      }

      this.preset = preset;
    },
    isSelectedPreset(preset) {
      return this.preset === preset
    }
  }
}
</script>

<style lang="scss" scoped>
.statistics-header {
  margin-bottom: 14px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);
  padding: 12px 18px;

  @include md() {
    padding: 12px 12px 8px;
  }

  /* Zeitraum-Feld: ruhiges Filled-Feld statt Outline-Rahmen */
  ::v-deep .v-text-field--outlined {
    fieldset {
      border: none;
    }

    .v-input__slot {
      background: #f4f5f7 !important;
      border-radius: 10px;
      min-height: 42px;
    }

    .v-label {
      font-size: 12px;
    }

    input {
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  /* Vergleichs-Umschalter als Pill-Gruppe */
  ::v-deep .v-btn-toggle {
    border-radius: 999px;
    background: #f4f5f7;
    padding: 2px;

    .v-btn {
      border: none !important;
      border-radius: 999px !important;
      background: transparent;
      height: 28px !important;

      &.v-btn--active {
        background: #fff !important;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
      }
    }
  }

  @media (max-width: 960px) {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;

    &__right {
      justify-content: space-between;
    }
  }

  /* Presets als ruhige Chips statt lauter Uppercase-Buttons */
  &__presets ::v-deep .v-btn {
    text-transform: none;
    letter-spacing: 0;
    font-size: 12.5px;
    font-weight: 600;
    border-radius: 999px;
  }

  ::v-deep .v-btn-toggle .v-btn {
    text-transform: none;
    letter-spacing: 0;
    font-size: 12px;
  }

  ::v-deep .v-text-field--outlined fieldset {
    border-color: rgba(0, 0, 0, 0.12);
  }

  &__range {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__comparison {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }

  &__comparison-label {
    font-size: 10.5px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.4);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
  }

  &__comparison-range {
    display: flex;
    align-items: center;
    font-size: 11px;
    font-weight: lighter;
    color: #757575;
    white-space: nowrap;
  }

  &__presets {
    display: flex;
    gap: 3px;
    align-items: center;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 14px;
  }
}
</style>
